import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { useQuote } from "./QuoteProvider";
import { CONTACT, PCB_TYPES } from "@/data/site";

const QUICK_LINKS = [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about" },
    { label: "Market Segments", href: "/market-segments" },
    { label: "Infrastructure", href: "/infrastructure" },
    { label: "PCB Gallery", href: "/gallery" },
    { label: "Quality", href: "/quality" },
    { label: "Contact Us", href: "/contact" },
];

export default function Footer() {
    const { openQuote } = useQuote();
    return (
        <footer className="bg-navy-dark text-slate-400">
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8">
                <div className="grid gap-12 md:grid-cols-12">
                    {/* Company */}
                    <div className="md:col-span-4">
                        <Logo dark />
                        <p className="mt-6 max-w-sm text-sm leading-relaxed">
                            Unimicron Technology India is a PCB manufacturing company based in Sahibabad,
                            Ghaziabad, providing quality PCB manufacturing solutions for electronics
                            manufacturers and industrial customers.
                        </p>
                        <div className="mt-6 space-y-3 font-mono text-[12.5px]">
                            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 transition-colors hover:text-white" data-testid="footer-email">
                                <Mail className="h-4 w-4 shrink-0 text-brand-red" /> {CONTACT.email}
                            </a>
                            <a href={CONTACT.phone1Href} className="flex items-center gap-3 transition-colors hover:text-white" data-testid="footer-phone-1">
                                <Phone className="h-4 w-4 shrink-0 text-brand-red" /> {CONTACT.phone1}
                            </a>
                            <a href={CONTACT.phone2Href} className="flex items-center gap-3 transition-colors hover:text-white" data-testid="footer-phone-2">
                                <Phone className="h-4 w-4 shrink-0 text-brand-red" /> {CONTACT.phone2}
                            </a>
                        </div>
                    </div>

                    {/* Quick links */}
                    <div className="md:col-span-2">
                        <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white">Quick Links</h3>
                        <ul className="mt-6 space-y-3 text-sm">
                            {QUICK_LINKS.map((l) => (
                                <li key={l.href + l.label}>
                                    <Link to={l.href} className="transition-colors hover:text-white" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* PCB links */}
                    <div className="md:col-span-3">
                        <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white">PCB Manufacturing</h3>
                        <ul className="mt-6 space-y-3 text-sm">
                            {PCB_TYPES.map((t) => (
                                <li key={t.id}>
                                    <Link to={`/pcb-manufacturing#${t.id}`} className="transition-colors hover:text-white" data-testid={`footer-link-${t.id}`}>
                                        {t.name}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <Link to="/pcb-manufacturing" className="inline-flex items-center gap-1 text-brand-red transition-colors hover:text-white">
                                    View all <ArrowUpRight className="h-3.5 w-3.5" />
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact / CTA */}
                    <div className="md:col-span-3">
                        <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-white">Contact</h3>
                        <p className="mt-6 flex gap-3 text-sm leading-relaxed">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                            {CONTACT.address}
                        </p>
                        <button
                            onClick={openQuote}
                            data-testid="footer-enquiry-btn"
                            className="mt-6 border border-brand-red bg-brand-red px-6 py-3 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                        >
                            Send Enquiry
                        </button>
                    </div>
                </div>
            </div>

            <div className="border-t border-navy-line">
                <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-2 px-4 py-6 font-mono text-[11px] uppercase tracking-wider sm:px-6 md:flex-row md:items-center lg:px-8">
                    <p data-testid="footer-copyright">
                        © {new Date().getFullYear()} {CONTACT.name}. All rights reserved.
                    </p>
                    <p className="text-slate-500">Sahibabad · Ghaziabad · Uttar Pradesh · India</p>
                </div>
            </div>
        </footer>
    );
}
